/**
 * Module dependencies,
 */

import { Breadcrumbs } from 'src/components/core/breadcrumbs';
import { CardSlide } from 'src/components/core/carousel';
import { Container } from 'src/components/core/layout/container';
import { DetailedTeamMember } from 'src/types/team-member';
import { GalleryCarousel } from 'src/components/layout/section/gallery-carousel';
import { GetStaticProps, NextPage } from 'next';
import { HorizontalTextBlocks } from 'src/components/layout/service/horizontal-text-blocks';
import { Lead } from 'src/components/layout/homepage/styles';
import { Locale } from 'i18n-routes';
import { ParallaxImage } from 'src/components/core/image';
import { PreviousAndNextGradientText } from 'src/components/layout/section/previous-and-next-gradient-text';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import { Text } from 'src/components/core/text';
import { TriggerNavbarTheme } from 'src/components/layout/navbar/trigger-navbar-theme';
import { defaultLocale } from 'src/core/constants/locales';
import { getLocalizedCategoryByServiceSlug } from 'src/core/utils/routes';
import { getTeam, getTeamMember } from 'src/requests/team';
import { media } from 'src/styles/utils/media';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useCallback, useMemo } from 'react';
import { useRouteResolver } from 'src/hooks/use-route-resolver';
import { useTranslation } from 'next-i18next';
import camelCase from 'lodash/camelCase';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  data: {
    locale: Locale;
    member: DetailedTeamMember;
  };
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  color: var(--color-gray700);
  padding: calc(var(--navbar-height) + 3rem) 0 var(--vertical-spacing);
  position: relative;

  ${media.min.ms`
    padding: calc(var(--navbar-height) + 5rem) 0 var(--vertical-spacing);
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${media.min.ms`
    grid-template-columns: 1fr 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Lead)`
  padding-right: 1rem;

  ${media.min.md`
    margin-right: 20%;
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Text)`
  margin-bottom: 2.5rem;
  padding-right: 1rem;

  ${media.min.md`
    margin-left: 20%;
  `}
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  overflow: hidden;
  padding-top: 129%;
  position: relative;
`;

/**
 * `StyledHorizontalTextBlocks` styled component.
 */

const StyledHorizontalTextBlocks = styled(HorizontalTextBlocks)`
  margin-top: 4rem;
`;

/**
 * `TeamMember` page.
 */

const TeamMember: NextPage<Props> = (props: Props) => {
  const { locale, member } = props.data;
  const { t } = useTranslation(['common', 'team']);
  const resolveRoute = useRouteResolver();
  const { career, nextSlug, previousSlug } = useMemo(
    () => ({
      career: !!member.professionalCareer && [
        {
          description: member.professionalCareer,
          title: t('team:detail.career')
        }
      ],
      nextSlug: member.nextStaffMember?.slug,
      previousSlug: member.previousStaffMember?.slug
    }),
    [member, t]
  );

  const getServiceBreadcrumbs = useCallback(
    (category: string, subCategory: string, hideLinks?: boolean) => [
      {
        label: t('common:terms.services'),
        url: hideLinks ? undefined : resolveRoute('allServices')
      },
      {
        label: t(`common:service.category.${category}`),
        url: hideLinks
          ? undefined
          : resolveRoute('serviceType', {
            slug: getLocalizedCategoryByServiceSlug(
                locale as Locale,
                category
            )
          })
      },
      {
        label: t(`common:service.subCategory.${camelCase(subCategory)}`)
      }
    ],
    [locale, resolveRoute, t]
  );

  return (
    <>
      <SEO
        description={member.descriptionSeo}
        openGraph={{
          description: member.descriptionSeo,
          locale,
          title: member.titleSeo
        }}
        title={member.titleSeo}
      />

      <TriggerNavbarTheme theme={'light'} />

      <Wrapper>
        <Container>
          <Breadcrumbs
            data-scroll={'fade-in-up'}
            items={[
              {
                label: t('team:list.lead'),
                url: resolveRoute('team')
              }
            ]}
          />

          <Grid>
            <div>
              <Title
                as={'h1'}
                data-scroll={'fade-in-up'}
                data-scroll-animation-delay={100}
              >
                {member.name}
              </Title>

              <Description
                as={'p'}
                data-scroll={'fade-in-up'}
                data-scroll-animation-delay={200}
              >
                {member.description}
              </Description>
            </div>

            <ImageWrapper>
              <ParallaxImage
                alt={`${member.name} image`}
                data-scroll={'clip-left'}
                data-scroll-animation-delay={300}
                data-scroll-delay={0.1}
                data-scroll-direction={'vertical'}
                data-scroll-speed={0.8}
                src={member?.image}
                style={{ objectPosition: 'top center' }}
              />
            </ImageWrapper>
          </Grid>

          {!!career && (
            <StyledHorizontalTextBlocks
              blocks={career}
              titleElement={'h2'}
            />
          )}
        </Container>

        {!!member.services.length && (
          <GalleryCarousel
            buttonLabel={t('common:sections.services.viewAll')}
            buttonUrl={resolveRoute('allServices')}
            slidesLength={member.services.length}
            title={t('team:detail.services.title')}
          >
            {member.services.map(
              (
                { category, highlightImage, internalTitle, slug, subCategory },
                index
              ) => (
                <CardSlide
                  data-scroll={'clip-left'}
                  data-scroll-animation-delay={index * 400}
                  data-scroll-offset={'20%'}
                  href={resolveRoute('service', { slug })}
                  imageUrl={highlightImage}
                  key={highlightImage}
                  lead={(
                    <Breadcrumbs
                      items={getServiceBreadcrumbs(category, subCategory, true)}
                    />
                  )}
                  title={internalTitle}
                />
              )
            )}
          </GalleryCarousel>
        )}

        {(!!previousSlug || !!nextSlug) && (
          <Container>
            <PreviousAndNextGradientText
              nextUrl={resolveRoute('teamMember', { slug: nextSlug })}
              previousUrl={resolveRoute('teamMember', { slug: previousSlug })}
            />
          </Container>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<SSGProps> = async ({
  locale,
  params
}) => {
  try {
    const member = await getTeamMember(
      locale as Locale,
      params?.slug as string
    );

    if (!member) {
      throw new Error('notFound');
    }

    return {
      props: {
        data: { locale, member },
        navbarTheme: 'light',
        ...(await serverSideTranslations(locale as string, ['common', 'team']))
      },
      revalidate: 60
    };
  } catch (error) {
    return {
      notFound: true
    };
  }
};

/**
 * Export `getStaticPaths`.
 */

export async function getStaticPaths() {
  const services = await getTeam(defaultLocale);
  const paths = services.map(({ slug }) => ({
    params: { slug }
  }));

  return {
    fallback: 'blocking',
    paths
  };
}

/**
 * Export `TeamMember` component.
 */

export default TeamMember;
